import { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import MsgBoxSelect from "../../MsgBoxSelect/msg-box-select.component";
import { getAccessToken } from "../../../helpers/functions";

/// Reapit journal type picker. The valid values live in the Reapit backend, which
/// the web app cannot reach directly, so they are fetched via the msgbox API
/// (GET /reapit/journalentrytypes/{customerId}). The customer id must be set first.
export const ReapitJournalTypeSelect = ({ customerId, value, onChange }) => {
	const { addToast } = useToasts();
	const [journalTypes, setJournalTypes] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!customerId) {
			setJournalTypes([]);
			return;
		}

		let cancelled = false;

		const fetchJournalTypes = async () => {
			setLoading(true);
			try {
				const token = await getAccessToken();
				const response = await fetch(
					`${process.env.REACT_APP_API_URL}reapit/journalentrytypes/${customerId}`,
					{
						method: "GET",
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);

				if (!response.ok) {
					throw new Error("Failed to load journal entry types");
				}

				const data = await response.json();
				if (cancelled) return;

				setJournalTypes(
					(data || []).map((type) => ({
						label: type.value,
						value: type.id,
					}))
				);
			} catch (error) {
				if (cancelled) return;
				console.error(
					"Error fetching Reapit journal entry types:",
					error
				);
				addToast("Failed to load Reapit journal entry types", {
					appearance: "error",
					autoDismiss: true,
				});
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		// debounce so we don't fire on every keystroke while the customer id is typed
		const handle = setTimeout(fetchJournalTypes, 500);

		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerId]);

	// react-select renders the label from the currently-selected value object, so
	// prefer the fetched option (friendly name) and fall back to the stored id.
	const selectedOption = value
		? journalTypes.find((option) => option.value === value) || {
				label: value,
				value,
		  }
		: null;

	return (
		<MsgBoxSelect
			label="Reapit Journal Type"
			name="reapitJournalType"
			options={journalTypes}
			value={selectedOption}
			onChange={onChange}
			isLoading={loading}
			isDisabled={!customerId}
			placeholder={
				customerId
					? "Select journal type..."
					: "Enter a Reapit Customer Id first"
			}
		/>
	);
};
